import { call, put, takeLatest, race, take } from 'redux-saga/effects';
import get from 'lodash/get';
import axiosConfig from './axiosConfig';
import {
  SAVE_REPORT_DRAFT,
  FETCH_REPORT_DRAFT,
  FETCH_REPORT_DRAFT_VERSIONS,
  CLEAR_REPORT_DRAFT
} from './types';
import {
  saveReportDraftSuccess,
  saveReportDraftFailure,
  fetchReportDraftSuccess,
  fetchReportDraftFailure,
  fetchReportDraftVersionsSuccess,
  fetchReportDraftVersionsFailure
} from './actions';
import { Notification } from '../../components/ui/custom-notification';

// API functions
const api = {
  // Save or update a draft
  saveDraft: async (payload) => {
    try {
      const url = '/reports/draft';
      const res = await axiosConfig.post(url, payload);
      
      if (res.status === 200 || res.status === 201) {
        return {
          success: true,
          draftId: get(res, ['data', 'data', 'draftId'], get(res, ['data', 'draftId'])),
          version: get(res, ['data', 'data', 'version'], get(res, ['data', 'version'])),
          isReadyToGenerate: get(res, ['data', 'data', 'isReadyToGenerate'], get(res, ['data', 'isReadyToGenerate'], false)),
          missingFields: get(res, ['data', 'data', 'missingFields'], get(res, ['data', 'missingFields'], [])),
          draft: get(res, ['data', 'data'], res.data)
        };
      }
      
      throw new Error('Failed to save draft');
    } catch (error) {
      if (error.response) {
        throw new Error(error.response.data?.message || 'Failed to save draft');
      }
      throw error;
    }
  },

  // Fetch the latest draft
  fetchDraft: async (payload) => {
    try {
      const { projectId, reportType, pipeId, pipeIds, productionId } = payload;
      
      let url = `/reports/draft?projectId=${projectId}&reportType=${reportType}`;
      
      if (pipeId) {
        url += `&pipeId=${pipeId}`;
      }
      
      if (pipeIds && pipeIds.length > 0) {
        url += `&pipeIds=${pipeIds.join(',')}`;
      }
      
      if (productionId) {
        url += `&productionId=${productionId}`;
      }
      
      const res = await axiosConfig.get(url);
      
      if (res.status === 200) {
        const draftData = get(res, ['data', 'data'], res.data);
        
        // If no draft data exists
        if (!draftData) {
          return { success: true, draft: null };
        }

        return {
          success: true,
          // formData contains the form fields
          draft: draftData.formData || null,
          // signatures are stored separately at root level
          draftSignatures: draftData.signatures || {},
          // metadata
          draftId: draftData.draftId || draftData.id,
          version: draftData.version,
          status: draftData.status,
          isReadyToGenerate: draftData.isReadyToGenerate || false,
          missingFields: draftData.missingFields || [],
          createdAt: draftData.createdAt,
          updatedAt: draftData.updatedAt
        };
      }
      
      // No draft found - return null (not an error)
      return { success: true, draft: null };
    } catch (error) {
      // 404 means no draft exists - this is not an error
      if (error.response?.status === 404) {
        return { success: true, draft: null };
      }
      if (error.response) {
        throw new Error(error.response.data?.message || 'Failed to fetch draft');
      }
      throw error;
    }
  },

  // Fetch all draft versions
  fetchDraftVersions: async (payload) => {
    try {
      const { projectId, reportType, pipeId, pipeIds, productionId } = payload;
      
      let url = `/reports/draft/versions?projectId=${projectId}&reportType=${reportType}`;
      
      if (pipeId) {
        url += `&pipeId=${pipeId}`;
      }
      
      if (pipeIds && pipeIds.length > 0) {
        url += `&pipeIds=${pipeIds.join(',')}`;
      }
      
      if (productionId) {
        url += `&productionId=${productionId}`;
      }
      
      const res = await axiosConfig.get(url);
      
      if (res.status === 200) {
        return get(res, ['data', 'data'], res.data) || [];
      }
      
      return [];
    } catch (error) {
      if (error.response?.status === 404) {
        return [];
      }
      if (error.response) {
        throw new Error(error.response.data?.message || 'Failed to fetch draft versions');
      }
      throw error;
    }
  }
};

// Sagas
function* saveDraftSaga(action) {
  try {
    const result = yield call(api.saveDraft, action.payload);
    yield put(saveReportDraftSuccess(result));
    Notification.success('Draft saved successfully!');
  } catch (error) {
    yield put(saveReportDraftFailure(error.message || 'Failed to save draft'));
    Notification.error('Failed to save draft', error.message || 'An error occurred');
  }
}

function* fetchDraftSaga(action) {
  try {
    const outcome = yield race({
      response: call(api.fetchDraft, action.payload),
      cancel: take(CLEAR_REPORT_DRAFT),
    });
    if (outcome.cancel) {
      return;
    }
    yield put(fetchReportDraftSuccess(outcome.response));
  } catch (error) {
    yield put(fetchReportDraftFailure(error.message || 'Failed to fetch draft'));
  }
}

function* fetchDraftVersionsSaga(action) {
  try {
    const result = yield call(api.fetchDraftVersions, action.payload);
    yield put(fetchReportDraftVersionsSuccess(result));
  } catch (error) {
    yield put(fetchReportDraftVersionsFailure(error.message || 'Failed to fetch draft versions'));
  }
}

// Watcher Saga
export default function* reportDraftsSaga() {
  yield takeLatest(SAVE_REPORT_DRAFT, saveDraftSaga);
  yield takeLatest(FETCH_REPORT_DRAFT, fetchDraftSaga);
  yield takeLatest(FETCH_REPORT_DRAFT_VERSIONS, fetchDraftVersionsSaga);
}

